import { SparklesIcon } from "@heroicons/react/24/outline"
import { Link } from "react-router-dom"
import { LoversLiveForeverLogo } from "components/LoversLiveForeverLogo"
import { FeatureSection } from "components/FeatureSection"
import { CTASection } from "components/CTASection"
import { Footer } from "components/Footer"
import { ButtonLink } from "components/ui"
import { classNames } from "utils/ClassNames"
import WomanReadingPhone from "assets/png/woman-reading-phone.png"
import ManReflecting from "assets/png/man-reflecting.png"
import ElderlyCoupleReading from "assets/png/elderly-couple-reading.png"
import YoungHappyWoman from "assets/png/young-happy-woman.png"
import YoungHappyMan from "assets/png/young-happy-man.png"

const galleryImages = [
  WomanReadingPhone,
  ManReflecting,
  ElderlyCoupleReading,
  YoungHappyWoman,
  YoungHappyMan
]

const SiteHeader = () => (
  <header className="sticky top-0 z-50 border-b border-gray-900/10 bg-white/80 backdrop-blur dark:border-white/10 dark:bg-gray-900/80">
    <nav
      aria-label="Global"
      className="mx-auto flex max-w-7xl items-center justify-between p-4 lg:px-8"
    >
      <Link to="/" className="-m-1.5 p-1.5">
        <span className="sr-only">Lovers Live Forever</span>
        <LoversLiveForeverLogo className="h-12 w-auto" />
      </Link>
      <div className="flex items-center gap-x-4 sm:gap-x-6">
        <Link
          to="/login"
          className="text-sm/6 font-semibold text-gray-900 dark:text-white"
        >
          Log in
        </Link>
        <ButtonLink to="/signup">Create account</ButtonLink>
      </div>
    </nav>
  </header>
)

const Hero = () => (
  <section className="relative isolate overflow-hidden">
    <div
      aria-hidden="true"
      className="absolute -top-40 left-1/2 -z-10 -translate-x-1/2 transform-gpu blur-3xl"
    >
      <div
        style={{
          clipPath:
            "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)"
        }}
        className="aspect-[1155/678] w-[72rem] bg-gradient-to-tr from-primary-400 to-[#F93F58] opacity-20 dark:opacity-15"
      />
    </div>

    <div className="mx-auto max-w-3xl px-6 pb-16 pt-20 text-center sm:pt-28 lg:px-8">
      <div className="mb-6 inline-flex items-center gap-x-2 rounded-full bg-gray-100 px-3 py-1 text-sm/6 font-medium text-gray-700 ring-1 ring-inset ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-white/10">
        <SparklesIcon
          aria-hidden="true"
          className="h-4 w-4 text-primary-600 dark:text-primary-400"
        />
        Digital time capsules
      </div>
      <h1 className="text-balance text-4xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-6xl">
        Preserve today's moments, surprise your future self
      </h1>
      <p className="mx-auto mt-6 max-w-2xl text-pretty text-lg/8 text-gray-600 dark:text-gray-300">
        Capture your dreams, hopes, and victories — then lock them away. Rediscover who
        you were and celebrate how far you've come.
      </p>
      <div className="mt-10 flex items-center justify-center gap-x-6">
        <ButtonLink to="/signup" size="lg">
          Create your account
        </ButtonLink>
        <Link
          to="/login"
          className="text-sm/6 font-semibold text-gray-900 dark:text-white"
        >
          Log in <span aria-hidden="true">→</span>
        </Link>
      </div>
    </div>

    <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-8">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        {galleryImages.map((src, index) => (
          <img
            key={src}
            alt=""
            src={src}
            className={classNames(
              "aspect-[3/4] w-full rounded-2xl object-cover shadow-sm ring-1 ring-gray-900/10 dark:ring-white/10",
              index % 2 === 1 ? "sm:translate-y-8" : "",
              index >= 4 ? "hidden sm:block" : ""
            )}
          />
        ))}
      </div>
    </div>
  </section>
)

export const HomePage = () => {
  return (
    <div className="bg-white dark:bg-gray-900">
      <SiteHeader />
      <main>
        <Hero />
        <FeatureSection />
        <CTASection />
      </main>
      <div className="border-t border-gray-900/10 dark:border-white/10">
        <Footer />
      </div>
    </div>
  )
}
